import { createSelector, createSlice } from '@reduxjs/toolkit'
import { isEmpty } from 'lodash'
import { useSelector } from 'react-redux'

import { LAYER_TYPES, selectLayerType } from '~map-preview/states/layerType'
import {
  selectMarkerCategory,
  selectMarkerType
} from '~map-preview/states/marker'

import { selectAggregateProperty as selectBoundaryAggregateProperty } from '../boundaries'
import { selectAggregateProperty as selectHexagonAggregateProperty } from '../hexagons'
import { MARKER_TYPES } from '../marker'

export const initialState = {
  title: '',
  classes: []
}

export const legendSlice = createSlice({
  name: 'legend',
  initialState,
  reducers: {
    setLegend: (state, action) => {
      const { title, classes } = action.payload
      state.classes = classes
      state.title = title
    }
  }
})

export const { setLegend } = legendSlice.actions

const selectClasses = (state) => state.legend.classes

const selectedLegend = createSelector([selectClasses], (classes) => {
  if (!classes.length) return []
  // Sort classes by the order of the current categories [most -> least]
  return [...classes].map((item) => ({
    ...item,
    label: isEmpty(item?.label) ? 'No value' : item.label
  }))
})

const selectedTitle = (state) => state.legend.title

const selectVisualisation = createSelector(
  [selectMarkerType, selectLayerType],
  (markerType, layerType) => {
    if (layerType === LAYER_TYPES.BOUNDARIES) return 'quantity'

    if (
      layerType === LAYER_TYPES.MARKER &&
      markerType === MARKER_TYPES.CATEGORIES
    )
      return 'category'

    return 'quantity'
  }
)

const aggregateTitle = {
  count: 'Location count'
}

const selectLegendDrawerTitle = createSelector(
  [
    selectLayerType,
    selectMarkerType,
    selectMarkerCategory,
    selectBoundaryAggregateProperty,
    selectHexagonAggregateProperty
  ],
  (
    layerType,
    markerType,
    markerCategory,
    boundaryAggregate,
    hexagonAggregate
  ) => {
    if (
      layerType === LAYER_TYPES.MARKER &&
      markerType === MARKER_TYPES.CATEGORIES
    ) {
      return markerCategory
    }

    if (layerType === LAYER_TYPES.BOUNDARIES) {
      return aggregateTitle[boundaryAggregate] || boundaryAggregate
    }

    if (layerType === LAYER_TYPES.HEXAGON) {
      return aggregateTitle[hexagonAggregate] || hexagonAggregate
    }
  }
)

export const useLegend = () => useSelector(selectedLegend)

export const useTitle = () => useSelector(selectedTitle)

export const useVisualisation = () => useSelector(selectVisualisation)

export const useLegendDrawerTitle = () => useSelector(selectLegendDrawerTitle)

export default legendSlice.reducer
